/**
 * Subscription service for handling Stripe subscription operations.
 * This service communicates with our backend API to manage subscriptions.
 */

import { BaseService } from './base-service';

export interface SubscriptionResponse {
  user_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface StripeCheckoutSession {
  session_id: string;
  session_url: string;
  expires_at: string;
}

export interface PaymentIntentResponse {
  client_secret: string;
  customer_id: string;
}

export class SubscriptionService extends BaseService {
  private subscriptionBaseUrl = '/subscriptions';

  constructor() {
    super('SubscriptionService');
  }

  /**
   * Get the current user's subscription status
   */
  async getSubscription(): Promise<SubscriptionResponse> {
    const response = await this.makeRequest<SubscriptionResponse>(`${this.subscriptionBaseUrl}/me`);
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }

  /**
   * Create a Stripe checkout session for subscription purchase
   */
  async createCheckoutSession(priceId: string): Promise<StripeCheckoutSession> {
    const response = await this.makeRequest<StripeCheckoutSession>(
      `${this.subscriptionBaseUrl}/checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ price_id: priceId }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }

  /**
   * Redirect to Stripe checkout
   */
  async redirectToCheckout(priceId: string): Promise<void> {
    try {
      const session = await this.createCheckoutSession(priceId);
      window.location.href = session.session_url;
    } catch (error) {
      console.error('Failed to create checkout session:', error);
      throw new Error('Failed to start checkout process');
    }
  }

  async createEmbeddedCheckout(priceId: string): Promise<PaymentIntentResponse> {
    const response = await this.makeRequest<PaymentIntentResponse>(
      `${this.subscriptionBaseUrl}/create-embedded-checkout`,
      {
        method: 'POST',
        body: JSON.stringify({ price_id: priceId }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }

  /**
   * Create a Stripe customer portal session
   * @returns Portal session with URL to redirect the user to
   */
  async createPortalSession(): Promise<{ portal_url: string; return_url: string }> {
    const response = await this.makeRequest<{ portal_url: string; return_url: string }>(
      `${this.subscriptionBaseUrl}/create-portal-session`,
      {
        method: 'POST',
      }
    );
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }

  /**
   * Create an anonymous embedded checkout session (no authentication required)
   * @param userEmail User's email address
   * @param priceId Stripe price ID for the subscription
   * @returns Checkout session with client secret for embedded checkout
   */
  async createAnonymousEmbeddedCheckout(
    userEmail: string,
    priceId: string
  ): Promise<PaymentIntentResponse> {
    const response = await this.makeRequest<PaymentIntentResponse>(
      `${this.subscriptionBaseUrl}/create-embedded-checkout-anonymous`,
      {
        method: 'POST',
        body: JSON.stringify({ user_email: userEmail, price_id: priceId }),
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    if (!response.success) {
      throw new Error(response.message);
    }
    return response.data;
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();
